import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { ProviderToken, Settings } from '../../lib/settings-types.js';
import {
  Button,
  Card,
  Checkbox,
  Combobox,
  CopyButton,
  EmptyState,
  Field,
  SecretField,
  Select,
  TextInput,
  Toolbar,
  useConfirm,
} from '../../components/ui/index.js';
import { ADMIN_NOTE, errorMessage, fmtWhen, useIsAdmin, useSettingsPost } from './common.js';

/**
 * APIs pane: the Anthropic key (column-matching), the Google OAuth client, and
 * provider bearer tokens (Airman lives on the Buy side pane). Every secret shows
 * a preview only; a minted token's full value is shown exactly once. Treat this
 * pane like a password page.
 */
export function KeysPane({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const { confirm, dialog } = useConfirm();

  const [gcid, setGcid] = useState(settings.googleClientId ?? '');
  const [sendTest, setSendTest] = useState(true);
  const [tokLabel, setTokLabel] = useState('');
  const [tokScope, setTokScope] = useState<'read' | 'write'>('read');
  const [tokRep, setTokRep] = useState('');
  const [minted, setMinted] = useState<{ label: string; token: string } | null>(null);

  const setAnthropicKey = useSettingsPost<{ key: string }>('/api/settings/anthropic-key');
  const setGoogleClient = useSettingsPost<{ clientId: string; clientSecret: string }>(
    '/api/settings/google-client',
  );
  const setAlertWebhook = useSettingsPost<{ url: string; test?: boolean }>(
    '/api/settings/alert-webhook',
  );

  const tokens = useQuery({
    queryKey: ['provider-tokens'],
    queryFn: () => api.get<{ tokens: ProviderToken[] }>('/api/provider-tokens'),
    enabled: isAdmin,
  });
  const mint = useMutation({
    mutationFn: () =>
      api.post<{ token: string; label: string }>('/api/provider-tokens', {
        label: tokLabel,
        scope: tokScope,
        ...(tokRep ? { ownerRep: tokRep } : {}),
      }),
    onSuccess: (res) => {
      setMinted({ label: res.label, token: res.token });
      setTokLabel('');
      void qc.invalidateQueries({ queryKey: ['provider-tokens'] });
    },
  });
  const toggle = useMutation({
    mutationFn: (t: { id: number; enabled: boolean }) => api.post('/api/provider-tokens/toggle', t),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['provider-tokens'] }),
  });

  if (!isAdmin) {
    return (
      <Card title="API keys & tokens">
        <EmptyState>{ADMIN_NOTE}</EmptyState>
      </Card>
    );
  }

  return (
    <div>
      <Card
        title="Anthropic API key"
        description="Used once per new report layout to propose the column matching."
      >
        <SecretField
          label="Anthropic API key"
          isSet={!!settings.anthropicKey}
          preview={settings.anthropicKey}
          placeholder="sk-ant-…"
          saveLabel="Save key"
          onSave={(key) => setAnthropicKey.mutateAsync({ key })}
        />
      </Card>

      <Card
        title="Google OAuth client"
        description={
          <>
            Client for connecting Google accounts (Sheets / Gmail). Redirect URI:{' '}
            <span className="mono">{settings.googleRedirectUri}</span>. The project must have the{' '}
            <strong>Google Sheets API</strong> enabled (and the Gmail API for inbox scanning): private
            sheets are read through it.
          </>
        }
      >
        <SecretField
          label="Client secret"
          isSet={settings.googleSecretSet}
          saveLabel="Save client"
          extra={
            <Field label="Client ID">
              {(aria) => (
                <TextInput
                  {...aria}
                  value={gcid}
                  placeholder="…apps.googleusercontent.com"
                  onChange={(e) => setGcid(e.target.value)}
                />
              )}
            </Field>
          }
          onSave={(clientSecret) => setGoogleClient.mutateAsync({ clientId: gcid, clientSecret })}
        />
      </Card>

      <Card
        title="Alerts"
        description={
          <>
            A daily digest pings this webhook when something needs a human — a source not pulled in
            25h, a failing account, a missing FX rate, or an unreachable inbox — and a layout that
            changed is posted the moment a pull finds it, once per change. Slack/Discord-style{' '}
            <span className="mono">{'{"text": …}'}</span> POST.
          </>
        }
      >
        <SecretField
          label="Webhook URL"
          isSet={settings.alertWebhookSet}
          // Not masked: it's a URL you need to be able to read back and check,
          // and hiding it behind dots only made it harder to paste correctly.
          mask={false}
          placeholder="https://hooks.slack.com/…"
          hint="Must be https."
          saveLabel={sendTest ? 'Save & send test' : 'Save'}
          extra={
            <Checkbox
              label="Send a test message after saving"
              checked={sendTest}
              onChange={(e) => setSendTest(e.target.checked)}
            />
          }
          onSave={(url) => setAlertWebhook.mutateAsync({ url, test: sendTest })}
          removeLabel="Remove webhook"
          onRemove={() => {
            void (async () => {
              const ok = await confirm<true>({
                title: 'Remove the alert webhook?',
                body: <p>The daily digest stops going anywhere. Nothing else changes.</p>,
                choices: [{ label: 'Remove it', value: true, variant: 'danger' }],
              });
              if (ok) setAlertWebhook.mutate({ url: '' });
            })();
          }}
        />
      </Card>

      <Card
        title="Provider API tokens"
        description="Bearer tokens partners use against the destinations push/read-back API. The full value is shown ONCE at mint — only a preview is stored."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mint.mutate();
          }}
        >
          <div className="cfg-grid">
            <Field label="Who is this token for?" required>
              {(aria) => (
                <TextInput
                  {...aria}
                  value={tokLabel}
                  onChange={(e) => setTokLabel(e.target.value)}
                />
              )}
            </Field>
            <Field label="Access">
              {(aria) => (
                <Select
                  {...aria}
                  value={tokScope}
                  onChange={(e) => setTokScope(e.target.value as 'read' | 'write')}
                >
                  <option value="read">Read — stats read-back only</option>
                  <option value="write">Write — can register destinations</option>
                </Select>
              )}
            </Field>
            <Field label="Scope to a rep" hint="Optional. Limits the token to one rep's feeds.">
              {(aria) => (
                <Combobox
                  {...aria}
                  value={tokRep}
                  onChange={setTokRep}
                  options={settings.reps.map((r) => ({ value: r, label: r }))}
                  placeholder="any rep"
                />
              )}
            </Field>
          </div>
          <Toolbar className="cfg-actions">
            <Button type="submit" loading={mint.isPending} disabled={!tokLabel.trim()}>
              Mint token
            </Button>
            {mint.isError ? (
              <span className="ui-field__error">{errorMessage(mint.error)}</span>
            ) : null}
          </Toolbar>
        </form>

        {minted ? (
          <div className="warnbox ok">
            <p>
              Token for <b>{minted.label}</b> — copy it now, it will not be shown again:
            </p>
            <div className="mono token-once">{minted.token}</div>
            <Toolbar className="cfg-actions">
              <CopyButton value={minted.token} label="Copy token" />
              <Button variant="ghost" onClick={() => setMinted(null)}>
                Done
              </Button>
            </Toolbar>
          </div>
        ) : null}

        {tokens.data?.tokens.length ? (
          <table className="stat-table">
            <thead>
              <tr>
                <th className="l">Label</th>
                <th className="l">Token</th>
                <th className="l">Access</th>
                <th className="l">Rep</th>
                <th>Created</th>
                <th className="l">By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.data.tokens.map((t) => (
                <tr key={t.id} className={t.enabled ? undefined : 'row-disabled'}>
                  <td className="l">{t.label}</td>
                  <td className="l mono">{t.preview}</td>
                  <td className="l">{t.scope}</td>
                  <td className="l">{t.ownerRep ?? 'any'}</td>
                  <td>{fmtWhen(t.created)}</td>
                  <td className="l">{t.createdBy ?? '—'}</td>
                  <td>
                    <Button
                      variant={t.enabled ? 'danger' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        void (async () => {
                          if (t.enabled) {
                            const ok = await confirm<true>({
                              title: `Disable the token for ${t.label}?`,
                              body: (
                                <p>
                                  Requests using it start failing immediately. You can re-enable it
                                  here.
                                </p>
                              ),
                              choices: [{ label: 'Disable it', value: true, variant: 'danger' }],
                            });
                            if (!ok) return;
                          }
                          toggle.mutate({ id: t.id, enabled: !t.enabled });
                        })();
                      }}
                    >
                      {t.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>No tokens minted yet.</EmptyState>
        )}
      </Card>

      {dialog}
    </div>
  );
}
